// ============================================
// NRS.02 — Triage Session Types
// ============================================
// Schema chain: Patient input → TriageData + AgeGateResult + DataCompletenessFlags init
// Contract: Age gating (18+), consent, reproductive status mapping per PRO.01
// ============================================

// ── Age Gating ──

export interface AgeGateResult {
  /** Whether the patient passed age gating (18+) */
  passed: boolean;
  /** Calculated age from date of birth */
  calculatedAge: number;
  /** Date of birth provided */
  dateOfBirth: string;
  /** Whether server-side verification was performed */
  serverVerified: boolean;
  /** Rejection reason if failed */
  rejectionReason?: 'under_18' | 'invalid_dob' | 'future_date';
}

// ── Consent ──

export type ConsentType = 'terms_of_service' | 'privacy_policy' | 'data_processing' | 'telehealth';

export interface ConsentRecord {
  type: ConsentType;
  version: string;
  acceptedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

// ── Reproductive Status ──

/**
 * Reproductive status values that MUST match PRO.01 contract.
 * All 5 values + male auto-set to 'not_applicable'.
 */
export type ReproductiveStatus =
  | 'not_applicable'
  | 'trying_to_conceive'
  | 'pregnant'
  | 'breastfeeding'
  | 'postmenopausal';

// ── Triage Data (extended from src/types) ──

export interface TriageData {
  sex: 'female' | 'male';
  dateOfBirth: string;
  age: number;
  reproductiveStatus: ReproductiveStatus;
  /** Qualitative concerns (e.g. ['fatigue', 'brain_fog', 'weight_changes']) */
  concerns?: string[];
}

// ── Data Completeness Flags ──

/**
 * DataCompletenessFlags — PRO.01-B08 interface contract.
 * Modal completion flags. Chat disclosures do NOT flip these.
 * Only modal submission does.
 */
export interface DataCompletenessFlags {
  sexConfirmed: boolean;
  birthDateConfirmed: boolean;
  medicationsProvided: boolean;
  allergiesProvided: boolean;
  conditionsProvided: boolean;
  pregnancyStatusProvided: boolean;
}

export const INITIAL_DATA_COMPLETENESS_FLAGS: DataCompletenessFlags = {
  sexConfirmed: false,
  birthDateConfirmed: false,
  medicationsProvided: false,
  allergiesProvided: false,
  conditionsProvided: false,
  pregnancyStatusProvided: false,
};

// ── Journey Creation ──

export interface JourneyCreationResult {
  journeyId: string;
  sessionId: string;
  triageData: TriageData;
  ageGate: AgeGateResult;
  consents: ConsentRecord[];
  dataCompletenessFlags: DataCompletenessFlags;
  createdAt: Date;
}

// ── Metrics ──

export interface TriageSessionMetrics {
  totalCalls: number;
  successRate: number;
  avgLatencyMs: number;
  errorsByType: Record<string, number>;
  accuracyOnGoldStandard: number;
}
